/*=== CORE IMPORTS ===*/
import { html } from 'lit';
import { BaseVersionDetailView } from './base-version-detail-view.js';
import { versionViewStyles } from './version-view-styles.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/breadcrumb-item/breadcrumb-item.js';
import '@shoelace-style/shoelace/dist/components/breadcrumb/breadcrumb.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/color-picker/color-picker.js';
import '@shoelace-style/shoelace/dist/components/copy-button/copy-button.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/radio-button/radio-button.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/relative-time/relative-time.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/*=== Q1 COMPONENTS ===*/
import '../../components/q1-markdown/q1-markdown.js';
import '../../components/q1-md-textarea/q1-md-textarea.js';
import '../../components/q1-member-chip/q1-member-chip.js';

/*=== Q1 MODULES ===*/
import { versionResponseStatusOptions } from '../../models/version-client-enums.js';
import { FormHostMixin } from '../../modules/form-host-mixin.js';
import { q1VersionFunctions } from '../../modules/q1-core-functions.js';
import { buildRefPathMap } from '../../modules/cache-helpers-client.js';
import {
  actionNounWords, membersInRoleWords, subjectWords, parentWords,
} from '../../models/word-client-helpers.js';

/*=== CONSTANTS ===*/

import {
  versionActions,
  versionActionsMap,
} from '../../models/version-client-actions.js'

const WORD_MAP = ({ subjectCollection, parentCollection, role, flowType, approvers }) => ({
  ...subjectWords(subjectCollection), // "subject" = the membership record = guildMember etc
  ...parentWords(parentCollection), // "parent" = the final subject = guild, project, etc
  ...membersInRoleWords(role),
  ...actionNounWords(flowType === 'invite' ? 'invitation' : 'request'),
  approvers: approvers?.length > 1 ? 'approvers' : 'approver',
  Approvers: approvers?.length > 1 ? 'Approvers' : 'Approver',
  responses: approvers?.length > 1 ? 'responses' : 'response',
  Responses: approvers?.length > 1 ? 'Responses' : 'Response',
  one_of_the_approvers: approvers?.length > 1 ? 'one of the approvers' : 'the approver',
});

const MEMBER_SEARCH_CACHE = 'all-members-search';

const CACHE_MAX_AGE = 10; // in minutes

/*=== VIEW CLASS DEFINITION ===*/

export class VersionView extends FormHostMixin(BaseVersionDetailView) {
  static styles = [
    ...super.styles,
    versionViewStyles,
  ];

  static properties = {
    ...super.properties,

    responseStatus: { type: String },
    response: { type: Object },
    
    responding: { type: Boolean },
    w: { type: Object }, // word map for the current version
    refPathMap: { type: Object },
  };

  constructor() {
    super();

    this.responseStatus = null; // store this separately for reactivity
    this.response = {};
    
    this.responding = false;
    this.w = {};
    this.refPathMap = {};
  };

  /*=== LIFECYCLE & INITIALIZATION METHODS ===*/
  
  async versionLoaded() {
    this.setViewMeta(`${this.title} - ${this.parent?.name} - q1 Network`);

    // Build the word map
    this.w = WORD_MAP({
      subjectCollection: this.version.subjectCollection,
      parentCollection: this.version.parentCollection,
      role: this.version.contentData?.role,
      flowType: this.flowType,
      approvers: this.approvers,
    });

    // Get the member search cache and build the refPathMap
    const memberSearchCache =
      await this.appElement.cacheManager.getCache(MEMBER_SEARCH_CACHE, CACHE_MAX_AGE);
    this.refPathMap = buildRefPathMap(memberSearchCache.data, 'members', 'members');

    // If the cache is new or stale, wait for it to refresh in a side thread
    if (memberSearchCache.isNewOrStale) {
      memberSearchCache.refreshComplete.then((cache) => {
        this.refPathMap = buildRefPathMap(cache.data, 'members', 'members');
      });
    }
  }

  /*=== ACTIONS & EVENT HANDLERS ===*/

  async respond() {
    if (!this.context.allowedActions.includes('respond')) return;

    const responseForm = this.shadowRoot.getElementById('response-form');
    if (!responseForm.reportValidity()) {
      this.showToast('Please fill out all required fields.', null, 'danger');
      return;
    }

    const { respond } = q1VersionFunctions;

    this.responding = true;

    try {
      const requestData = {
        flowKey: this.version.approvalFlow,
        relatedDocIds: this.relatedDocIds,
        versionNumber: this.version.versionNumber,
        response: {
          memberId: this.currentMember.id,
          status: this.responseStatus,
          note: this.response.note,
          timestamp: new Date().toISOString(),
          opParams: this.showProfileFields
            ? this.response.opParams || {}
            : {},
        },
      };

      const response = await respond(requestData);

      this.showToast('Response submitted successfully!', null, 'success');

      this._refreshVersion();
    } catch (error) {
      console.error('Error responding:', error);
      this.showToast(error.message, 'Error submitting response!', 'danger');
    } finally {
      this.responding = false;
    }
  }

  /*=== GETTERS ===*/

  /** Returns true if current member is the target of the subject doc. */
  get isTarget() {
    if (!this.version?.id || !this.currentMember?.id) return false;

    return this.version.id.includes(this.currentMember.id);
  }

  get title() {
    let flowLabel = this.version?.approvalFlow_label || '';
    if (flowLabel) flowLabel += ': ';

    let subtitle = this.proposer?.name || '';
    if (!subtitle) subtitle = 'Doc Version';

    return `${flowLabel}${subtitle}`;
  }

  get relatedDocIds() {
    // Note that the 'subject' is technically the membership record not the final subject
    if (!this.version?.subjectId || !this.version?.subjectCollection) return {};

    const subjectIdParts = this.version.subjectId.split(':');

    switch (this.version.subjectCollection) {
      case 'guildMembers':
        return {
          guildId: subjectIdParts[0],
          memberId: subjectIdParts[1],
        };

      case 'projectMembers':
        return {
          projectId: subjectIdParts[0],
          memberId: subjectIdParts[1],
        };
      
      case 'guildProjects':
        return {
          guildId: subjectIdParts[0],
          projectId: subjectIdParts[1],
        };

      default:
        return {};
    }
  }

  /**
   * Possible flow types: `invite`, `request`, FIXME more to come
   */
  get flowType() {
    if (!this.version?.approvalFlow) return '';

    // Example Approval Flow: 'guildMembers.invite'
    // Example Return: 'invite'

    return this.version?.approvalFlow.split('.')[1];
  }

  get showProfileFields() {
    // IMPROVEME: This should rely on opParams via a version function.
    return this.responseStatus === 'approved'
      && this.flowType === 'invite'
      && (
        this.version?.versionNumber === 1
        || !this.subject?.headline?.length
      );
  }

  get proposerName() {
    return this.proposer?.name || '';
  }

  get approverName() {
    return this.approvers?.[0]?.name || '';
  }

  get subtitle() {
    if (!this.version || !this.context) return '';

    const w = this.w;
    const You_are = this.version.approvalStatus === 'proposed' ? "You are" : "You were";
    const is = this.version.approvalStatus === 'proposed' ? "is" : "was";
    const your = this.isTarget ? "your" : "this";
    const Your = this.isTarget ? "Your" : "This";
    const you = this.isTarget ? "you" : "this member";

    if (this.flowType === 'invite') {
      return (this.context.editPersona === 'approver')
        ? `${You_are} invited to join the ${w.parent} as ${w.a_member}.`
        : `${this.approverName} ${is} invited to join the ${w.parent} as ${w.a_member}.`;
    }

    if (this.flowType === 'request') {
      return (this.context.editPersona === 'proposer')
        ? `${You_are} requesting to join the ${w.parent} as ${w.a_member}.`
        : `${this.proposerName} ${is} requesting to join the ${w.parent} as ${w.a_member}.`;
    }

    // Note: Deactivation always auto approves, so it's always past tense.
    if (this.flowType === 'deactivate') {
      return (this.context.editPersona === 'proposer')
        ? `You de-activated ${your} ${w.parent} membership.`
        : `${this.proposerName} de-activated ${your} ${w.parent} membership.`;
    }

    // Note: Point onboarding always auto approves, so it's always past tense.
    if (this.flowType === 'point-onboard') {
      return (this.version.versionNumber === 1)
        ? `${Your} membership was automatically created as part of the ${w.parent} creation.`
        : `${this.proposerName} set ${you} as the new ${w.parent} point.`;
    }

    // Note: Point onboarding always auto approves, so it's always past tense.
    if (this.flowType === 'point-offboard') 
      return `${Your} membership was automatically updated when a new point was set.`;
    
    return `This version is a ${this.flowType} ${w.action}.`;
  }

  get isApprover() {
    return this.context.editPersona === 'approver';
  }

  get isActiveApprover() {
    return this.isApprover && this.version.approvalStatus === 'proposed';
  }

  get canSeeApprovalContent() {
    return this.context?.visibleData?.includes('approval-content') || false;
  }

  get detailViewPath() {
    return this.version?.detailViewPath || '';
  }

  get metaContext() {
    return { version: this.version };
  }  
  
  /*=== RENDER TEMPLATES ===*/

  get viewModeTemplate_loaded() {
    return html`
      ${this.errorSectionTemplate}

      ${!this.version ? '' : html`
        ${this.headerSectionTemplate}

        ${this.detailsSectionTemplate}

        ${this.responsesSectionTemplate}

        ${this.historySectionTemplate}
      `}
    `;
  }

  /*=== SECTION TEMPLATES ===*/

  get errorSectionTemplate() {
    if (!this.error) return null;

    return html`
      <section class="error">
        <section-content>
          <sl-alert variant="danger" open class="section-item full">
            <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
            ${this.errorMessage}
          </sl-alert>
        </section-content>
      </section>
    `;
  }

  get headerSectionTemplate() {
    return html`
      <section class="header">
        <section-content>
          <header-bar class="small section-item full align-center">
            <div class="icon-wrapper">
              <sl-icon
                class="primary-header-icon"
                library=${this.version.approvalFlow_iconLibrary}
                name=${this.version.approvalFlow_icon}
              ></sl-icon>
            </div>
            
            <h1>
              <a
                class="prefix"
                href=${this.subject?.detailViewPath}
              >${this.w.Subject}: ${this.subject?.name}</a>
              Version ${this.version.versionNumber}: ${this.version.approvalFlow_label}
            </h1>

            <sl-copy-button
              id="share-link" value=${`${location.origin}${this.detailViewPath}`}
              copy-label="Copy link to this page"
              success-label="Link copied!"
            >
              <sl-icon slot="copy-icon" name="link" library="material"></sl-icon>
              <sl-icon slot="success-icon" name="check" library="material"></sl-icon>
            </sl-copy-button>
          </header-bar>
        </section-content>
      </section>
    `;
  }

  get detailsSectionTemplate() {
    return html`
      <section class="details">
        <section-content>
          ${this.pendingResponseAlertItemTemplate}

          ${this.versionDetailItemTemplate}

          ${this.versionContentDataItemTemplate}

          ${this.proposalItemTemplate}
        </section-content>
      </section>
    `;
  }

  get responsesSectionTemplate() {
    return html`
      <section class="responses">
        <section-content>
          ${this.responsesListItemTemplate}

          ${this.pendingResponseItemTemplate}
        </section-content>
      </section>
    `;
  }

  get historySectionTemplate() {
    return html`
      <section class="history">
        <section-content>
        <div class="section-item half table-content">
            <h2>Status History</h2>

            <table class="info-table">
              ${this._infoTableRow_timestamp({
                label: 'Created',
                timestamp: this.version.createdAt,
                memberRef: this.version.createdBy,
              })}

              ${this._infoTableRow_timestamp({
                label: 'Locked',
                timestamp: this.version.lockedAt,
              })}

              ${this._infoTableRow_timestamp({
                label: 'Adopted',
                timestamp: this.version.adoptedAt,
              })}

              ${this._infoTableRow_timestamp({
                label: 'Abandoned',
                timestamp: this.version.abandonedAt,
                hideIfTimestampBlank: true,
              })}
            </table>
          </div>

          <div class="section-item half table-content last-item">
            <h2>Approval History</h2>

            <table class="info-table">
              ${this._infoTableRow({
                label: 'Approval Flow',
                value: html`<code>${this.version.approvalFlow_versionKey}</code>`,
              })}

              ${this._infoTableRow({
                label: 'Approval Method',
                value: html`<code>${this.version.approvalMethod}</code>`,
              })}

              ${this._infoTableRow_timestamp({
                label: 'Proposed',
                timestamp: this.version.proposedAt,
                memberRef: this.version.proposedBy,
              })}

              ${this._infoTableRow_timestamp({
                label: 'Approved',
                timestamp: this.version.approvedAt,
                memberRef: this.version.approvedBy,
              })}

              ${this._infoTableRow_timestamp({
                label: 'Declined',
                timestamp: this.version.declinedAt,
                memberRef: this.version.declinedBy,
                hideIfTimestampBlank: true,
              })}
            </table>
          </div>
        </section-content>
      </section>  
    `;
  }

  /*=== SECTION ITEM TEMPLATES ===*/

  get pendingResponseAlertItemTemplate() {
    if (!this.isActiveApprover) return null;

    return html`
      <div class="section-item full alert-content">
        <sl-icon name="warning" library="material"></sl-icon>
        <span class="alert-message">
          You are ${this.w.one_of_the_approvers} of this pending ${this.w.action}.
          It is waiting on your response.
        </span>

        <sl-button
          size="small"
          variant="primary"
          @click=${() => this.scrollToCenterOf('section.responses')}
          class="sl-themed-secondary"
          pill outline
        >
          <sl-icon name="keyboard_double_arrow_down" library="material" slot="prefix"></sl-icon>
          Respond Below
          <sl-icon name="keyboard_double_arrow_down" library="material" slot="suffix"></sl-icon>
        </sl-button>
      </div>
    `;
  }

  get versionDetailItemTemplate() {
    return html`
      <div class="section-item half table-content">
        <h2>Version Details</h2>

        <table class="info-table">
          ${this._infoTableRow_link({
            label: 'Subject Record',
            href: this.subject?.detailViewPath,
            text: `${this.w.Subject}: ${this.subject?.name}`,
          })}

          ${this._infoTableRow_link({
            label: 'Parent Record',
            href: this.parent?.detailViewPath,
            text: `${this.w.Parent}: ${this.parent?.name}`,
          })}

          ${this._infoTableRow_memberList({
            label: 'Proposer',
            memberRefs: this.version.proposer ? [this.version.proposer] : [],
          })}

          ${this._infoTableRow_memberList({
            label: 'Approvers',
            memberRefs: this.version.approvers,
          })}

          ${this._infoTableRow_tag({
            label: 'Status',
            icon: this.version.status_icon,
            iconLibrary: this.version.status_iconLibrary,
            text: this.version.status_label,
            tooltip: this.version.status_description,
            tagClass: this.version.status,
          })}

          ${this._infoTableRow_tag({
            label: 'Approval Status',
            icon: this.version.approvalStatus_icon,
            iconLibrary: this.version.approvalStatus_iconLibrary,
            text: this.version.approvalStatus_label,
            tooltip: this.version.approvalStatus_description,
            tagClass: this.version.approvalStatus,
          })}
        </table>
      </div>
    `;
  }

  get versionContentDataItemTemplate() {
    const contentData = this.version.contentData || {};
    const contentDataString = Object.keys(contentData).length
      ? JSON.stringify(contentData, null, 2)
      : "{\n  // NONE\n}";
    
    return html`
      <div class="section-item half data-content">
        <h2>Version Content Data</h2>

        <pre>${contentDataString}</pre>
      </div>
    `;
  } 

  get proposalItemTemplate() {
    return html`
      <div class="section-item full text-content">
        <h2>Proposal</h2>

        <p class="subtitle hero">
          ${this.subtitle}
        </p>

        <div class="label">${this.w.Action} Note</div>
        
        <blockquote>
          ${this.canSeeApprovalContent ? html`
            <q1-markdown .content=${this.version.proposerNote_withQuotes}></q1-markdown>
            <cite>
              <q1-member-chip .member=${this.proposer} signed-in></q1-member-chip>
            </cite>
          ` : html`
            <div class="empty-placeholder">
              <sl-icon library="material" name="visibility_off"></sl-icon>
              <span>Content not visible</span>
            </div>
          `}
        </blockquote>
      </div>
    `;
  }

  get responsesListItemTemplate() {
    return html`
      <div class="section-item text-content">
        <h2>${this.w.Responses}</h2>

        <simple-list>
          ${!this.canSeeApprovalContent ? html`

            <div class="list-item empty-placeholder">
              <sl-icon library="material" name="visibility_off"></sl-icon>
              <span>Content not visible</span>
            </div>

          ` : html`
          
            ${!this.version.responses?.length ? html`
              <div class="list-item empty-placeholder">
                <sl-icon library="material" name="block"></sl-icon>
                <span>This ${this.w.action} has no ${this.w.responses} yet.</span>
              </div>
            ` : this.version.responses.map(response => html`
              <div class="list-item">
                <div class="item-header">
                  <sl-tag class=${response.status}>
                    <sl-icon
                      library=${response.status_iconLibrary}
                      name=${response.status_icon}
                    ></sl-icon>
                    ${response.status_label}
                  </sl-tag>
                </div>

                <div class="item-body">
                  <q1-markdown .content=${response.note_withQuotes} with-quotes></q1-markdown>
                </div>

                <div class="item-footer">
                  <sl-format-date
                    date=${response.timestamp}
                    month="short" day="numeric" year="numeric"
                  ></sl-format-date>
                  <span>by</span>
                  <q1-member-chip
                    member-ref=${response.memberRef}
                    .refPathMap=${this.refPathMap}
                    signed-in
                  ></q1-member-chip>
                </div>
              </div>
            `)}

          `}
        </simple-list>
      </div>
    `;
  }

  get pendingResponseItemTemplate() {
    if (this.version.approvalStatus !== 'proposed') return '';

    return html`
      <div class="section-item text-content">
        ${!this.isActiveApprover ? html`

          <h3>Response is Pending</h3>

          <p class="subtitle hero">
            This ${this.w.action} is waiting on the response of the ${this.w.approvers}.
          </p>

        ` : html`

          <h3>Your Response</h3>

          <p class="subtitle hero">
            You can respond to this ${this.w.action} by filling out the form below.
          </p>

          <form id="response-form" class="q1 sl-themed-secondary">
            <sl-radio-group
              id="responseStatus"
              name="responseStatus"
              label="Response"
              value=${this.response.status}
              @sl-change=${this.handleChange}
              required
            >
              ${versionResponseStatusOptions.map((option) => html`
                <sl-radio-button value=${option.value}>
                  <sl-icon slot="prefix" library=${option.iconLibrary} name=${option.icon}></sl-icon>
                  ${option.label}
                </sl-radio-button>
              `)}
            </sl-radio-group>

            <q1-md-textarea
              id="responseNote"
              name="response.note"
              label="Response Note"
              value=${this.response.note || ''}
              @change=${this.handleChange}
              maxlength="2000"
              rows="5"
              resize="auto"
              ?disabled=${this.responding}
              required
            ></q1-md-textarea>

            ${!this.showProfileFields ? '' : html`

              <sl-divider class="full-width"></sl-divider>

              <h3>Your ${this.w.Parent} Profile</h3>

              <p class="subtitle hero">
                To accept this ${this.w.action}, please fill out your ${this.w.parent} profile.
                You'll be able to update this as often as you like, but it's good to get something
                basic in place so you can be properly introduced
                to the other ${this.w.parent} members.
              </p>

              <sl-input
                id="profileHeadline"
                name="response.opParams.headline"
                label="Profile Headline"
                value=${this.response.opParams?.headline || ''}
                @sl-change=${this.handleChange}
                maxlength="250"
                ?disabled=${this.responding}
                required
              ></sl-input>
      
              <q1-md-textarea
                id="profileBody"
                name="response.opParams.profile"
                label="Profile Body"
                value=${this.response.opParams?.profile || ''}
                @change=${this.handleChange}
                maxlength="2000"
                rows="15"
                resize="auto"
                ?disabled=${this.responding}
                required
              ></q1-md-textarea>

            `}

            <button-bar>
              <sl-button
                variant="primary"
                size="large"
                @click=${this.respond}
                ?disabled=${this.responding}
              >
                ${this.responding ? html`
                  <sl-spinner slot="prefix"></sl-spinner>
                  Processing...
                ` : html`
                  <sl-icon slot="prefix" library="material" name="rule"></sl-icon>
                  Submit Response
                `}
              </sl-button>
            </button-bar>
          </form>
        `}
      </div>
    `;
  }

  /*=== TEMPLATE HELPERS ===*/

  /** Label is required. Value can be string or html. */
  _infoTableRow({ label, value, hideIfValueBlank = false }) {
    if (hideIfValueBlank && !value) return null;

    return html`
      <tr>
        <td class="label-col">${label}</td>
        <td class="value-col">
          <span class="td-inner">
            ${value}
          </span>
        </td>
      </tr>
    `;
  }

  /** Label is required. Timestamp & member ref can be blank. */
  _infoTableRow_link({ label, href, text }) {
    return this._infoTableRow({
      label,
      value: html`<a href=${href}>${text}</a>`,
    });
  }

  /** Label is required. Member refs is an array of refs, or can be blank. */
  _infoTableRow_memberList({ label, memberRefs, hideIfBlank = false }) {
    if (hideIfBlank && !memberRefs?.length) return null;

    return this._infoTableRow({
      label,
      value: html`
        ${(memberRefs || []).map(memberRef => html`
          <q1-member-chip
            member-ref=${memberRef}
            .refPathMap=${this.refPathMap}
            signed-in
          ></q1-member-chip>
        `)}
      `,
    });
  }

  /** Everything is required except for tooltip and tagClass. */
  _infoTableRow_tag({ label, icon, iconLibrary, text, tooltip, tagClass }) {
    const tagTemplate = html`
      <sl-tag class=${`info-table-tag ${tagClass || ''}`}>
        <sl-icon library=${iconLibrary} name=${icon}></sl-icon>
        ${text}
      </sl-tag>
    `;

    return this._infoTableRow({
      label,
      value: tooltip ? html`
        <sl-tooltip content=${tooltip}>${tagTemplate}</sl-tooltip>
      ` : tagTemplate,
    });
  }

  /** Label is required. Timestamp & member ref can be blank. */
  _infoTableRow_timestamp({ label, timestamp, memberRef, hideIfTimestampBlank = false }) {
    if (hideIfTimestampBlank && !timestamp) return null;

    return this._infoTableRow({
      label,
      value: html`
        ${!timestamp ? '-' : html`
          <sl-format-date
            date=${timestamp}
            month="short" day="numeric" year="numeric"
            hour="numeric" minute="numeric"
          ></sl-format-date>
        `}
        
        ${!memberRef ? '' : html`
          <span>by</span>
          <q1-member-chip
            member-ref=${memberRef}
            .refPathMap=${this.refPathMap}
            signed-in
          ></q1-member-chip>
        `}
      `,
    });
  }
  
}

customElements.define('version-view', VersionView);
